import { Seo, breadcrumbLd, MATTHEW_LD } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink, LinkBtnGold, Hairline } from "../components/atoms";
import { BrochureLink } from "../components/BrochureLink";
import { MATTHEW, MATTHEW_PHOTO, TRACK_RECORD, CORPORATE } from "../data/content";

const Team = [
  { name: "Matthew", role: "Advice Lead", photo: MATTHEW_PHOTO, alt: "Matthew Clark holding his two bestselling books on property investment", framed: true },
  { name: "Sallie", role: "Client Support", photo: "/assets/team-sallie.webp", alt: "Sallie McLynskey, Client Support at 80 Metrics", framed: false },
  { name: "Isah", role: "Client Support", photo: "/assets/team-isah.webp", alt: "Isah Ragondo, Client Support at 80 Metrics", framed: false },
];

export default function About() {
  return (
    <>
      <Seo
        title="About — The people behind your advice | 80 Metrics"
        description="Matthew Clark leads property strategy and advice at 80 Metrics, supported by Sallie and Isah on client engagement and delivery. Independent by design."
        jsonLd={[MATTHEW_LD, breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>About</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>The people behind</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="italic text-gold">your advice.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
              The team supporting your decisions.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-charcoal/65 max-w-2xl leading-relaxed">
              Matthew leads the property strategy and advice. Sallie and Isah support the client engagement and delivery.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {Team.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div data-testid={`team-${p.name.toLowerCase()}`}>
                  {p.framed ? (
                    <div className="relative">
                      <div className="absolute -top-3 -left-3 w-full h-full border border-gold" aria-hidden="true" />
                      <div className="relative aspect-[3/4] overflow-hidden bg-bone">
                        <img src={p.photo} alt={p.alt} width="1500" height="2000" className="h-full w-full object-cover object-top" loading="lazy" data-testid={`team-photo-${p.name.toLowerCase()}`} />
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-[3/4] overflow-hidden bg-bone">
                      <img src={p.photo} alt={p.alt} width="1500" height="2000" className="h-full w-full object-cover object-top" loading="lazy" data-testid={`team-photo-${p.name.toLowerCase()}`} />
                    </div>
                  )}
                  <h3 className="mt-6 font-serif text-2xl font-medium text-charcoal">{p.name}</h3>
                  <p className="mt-1 text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">{p.role}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20 lg:mt-28 max-w-3xl">
            <Hairline />
            <div className="py-10">
              <h3 className="font-serif text-3xl lg:text-4xl font-medium text-charcoal">{MATTHEW.name}</h3>
              <p className="mt-2 text-sm font-semibold tracking-[0.18em] uppercase text-charcoal/70">{MATTHEW.role}</p>
              <p className="mt-5 text-sm text-charcoal/75 leading-relaxed">{MATTHEW.quals}</p>
              <p className="mt-2 text-xs text-charcoal/55 leading-relaxed">{CORPORATE.trading_line} · {CORPORATE.abn} · {CORPORATE.licence}</p>
              <p className="mt-5 font-serif text-xl italic text-golddark">{MATTHEW.author}</p>
            </div>
            <Hairline />
          </Reveal>
        </div>
      </section>

      <section className="bg-bone" data-testid="about-track-record">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28">
          <Reveal>
            <Eyebrow>Track record</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal max-w-2xl">
              Property, finance and evidence — in one adviser.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-charcoal/15 border border-charcoal/15">
            {TRACK_RECORD.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.07} className="bg-ivory p-8 lg:p-12">
                <span className="text-xs font-semibold tracking-[0.3em] uppercase text-golddark">{t.label}</span>
                <p className="mt-4 text-sm lg:text-base leading-relaxed text-charcoal/75">{t.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-12 text-sm lg:text-base leading-relaxed text-charcoal/65 max-w-3xl">
              Matthew’s background matters because property decisions rarely exist in isolation. Price, borrowing
              capacity, holding costs, risk and opportunity cost often interact — and the best decision is not always
              the one that creates a transaction.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 border border-gold/50 bg-ivory px-6 lg:px-10 py-8" data-testid="about-research-note">
              <p className="text-[11px] font-semibold tracking-[0.26em] uppercase text-golddark">
                Multiple sources · Specialist analysis · Relevant evidence
              </p>
              <p className="mt-4 text-sm leading-relaxed text-charcoal/70 max-w-3xl">
                Research partners and multiple data providers contribute evidence to our analysis. Matthew interprets
                that evidence in the context of your question and circumstances.
              </p>
              <div className="mt-5">
                <ArrowLink to="/how-we-work" data-testid="about-framework-link">See how our research framework works</ArrowLink>
              </div>
              <div className="mt-5">
                <BrochureLink />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal text-ivory" data-testid="about-independence">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow dark>Independent by design</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Our agenda is yours.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm lg:text-base leading-relaxed text-ivory/70">
              We work for you and are paid by you. 80 Metrics charges fixed professional fees — no selling-agent
              commissions, no referral fees, no developer commissions or project rebates — and we do not share in
              your selling agent’s commission. Advice does not have to lead to a transaction: an Assessment may
              recommend retaining, and advocacy begins only if you choose to act.
            </p>
            <p className="mt-6 font-serif text-xl lg:text-2xl italic text-gold">
              Your goals and circumstances guide the advice.
            </p>
            <p className="mt-8 text-sm lg:text-base leading-relaxed text-ivory/70">
              Independent advice starts with understanding the decision. Tell us what you’re weighing up — we’ll help
              you identify the right starting point, or tell you if you don’t need a paid service.
            </p>
            <div className="mt-10">
              <LinkBtnGold to="/contact" data-testid="about-cta">Discuss my next move</LinkBtnGold>
            </div>
            <p className="mt-5 text-xs tracking-wide text-ivory/45">
              Scope and fees are agreed before any paid work begins.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
